import { modal } from '@/js/App/PdModal/PdModal'
import A11yDialog from 'a11y-dialog'
import { Extension, InteractionEvent, Naja, SuccessEvent } from 'naja'

declare module 'naja' {
	interface Options {
		cookieConsentForm?: HTMLFormElement
	}
}

export class CookieConsentExtension implements Extension {
	private element: HTMLElement | null
	private a11yDialog: A11yDialog | undefined
	private static readonly selector = '.js-cookie-consent'
	private static readonly formSelector = `${CookieConsentExtension.selector}__form`

	public constructor() {
		this.element = document.querySelector<HTMLElement>(CookieConsentExtension.selector)

		const form = this.element?.querySelector<HTMLFormElement>(CookieConsentExtension.formSelector)

		if (!this.element || !form) {
			return
		}

		this.a11yDialog = new A11yDialog(this.element)
		this.a11yDialog.show()
	}

	public initialize(naja: Naja): void {
		naja.uiHandler.addEventListener('interaction', this.checkExtensionEnabled.bind(this))
		naja.addEventListener('success', this.handleSuccess.bind(this))
	}

	// The form isn't necessarily the one found in the constructor – it may as well come from a modal
	// opened later on. Instead of keeping it on the instance, we pass it along with the request
	// options, so that each request is handled with the form it has really been submitted from.
	private checkExtensionEnabled(event: InteractionEvent): void {
		const { element, options } = event.detail
		const form = (element as HTMLInputElement).form

		if (form?.classList.contains(CookieConsentExtension.formSelector.substring(1))) {
			options.cookieConsentForm = form
		}
	}

	private handleSuccess(event: SuccessEvent): void {
		const { options } = event.detail

		if (!options.cookieConsentForm) {
			return
		}

		const consentCategories = this.getAcceptedCategories(options.cookieConsentForm)

		if (consentCategories.length) {
			const scripts = this.getScripts(consentCategories)
			this.runScripts(scripts)
		}

		this.closeCookieConsent()
	}

	private getAcceptedCategories(form: HTMLFormElement): string[] {
		const categories: string[] = []
		const acceptAll: boolean =
			form['nette-submittedBy'] !== undefined && form['nette-submittedBy'].dataset.cookieConsentAcceptAll !== undefined
		const rejectAll: boolean =
			form['nette-submittedBy'] !== undefined && form['nette-submittedBy'].dataset.cookieConsentRejectAll !== undefined

		if (rejectAll) {
			return []
		}

		for (let i = 0; i < form.elements.length; i++) {
			const input = form.elements[i] as HTMLInputElement
			const consent = input.dataset.cookieConsentCategory

			if (consent && (acceptAll || input.checked)) {
				categories.push(consent)
			}
		}

		return categories
	}

	private getScripts(categories: string[]): NodeListOf<HTMLScriptElement> {
		const selectors: string[] = []

		categories.forEach(function (category) {
			selectors.push('script[data-cookie-consent="' + category + '"]')
		})

		return document.querySelectorAll<HTMLScriptElement>(selectors.join(','))
	}

	private runScripts(scripts: NodeListOf<HTMLScriptElement>): void {
		scripts.forEach((script) => this.runScript(script))
	}

	private runScript(script: HTMLScriptElement): void {
		const scriptRunnable = document.createElement('script')

		if (script.src) {
			scriptRunnable.src = script.src
		} else {
			scriptRunnable.innerHTML = script.innerHTML
		}

		scriptRunnable.async = script.async
		scriptRunnable.defer = script.defer

		scriptRunnable.crossOrigin = script.crossOrigin
		scriptRunnable.referrerPolicy = script.referrerPolicy

		script.insertAdjacentElement('afterend', scriptRunnable)
		script.remove()
	}

	// The consent form may live in one of two containers, each of them being closed differently:
	//
	//   1. The cookie bar rendered right into the page on the first visit. We own the element (found
	//      in the constructor), so running the closing animation and removing the element from the
	//      DOM is up to us.
	//   2. The pd-modal opened by the link in the footer. There the element belongs to the modal, so
	//      we just ask it to close and let it handle its own dataset and the removal. Setting
	//      `data-modal-closing` ourselves would only break the closing animation, as the modal sets
	//      the very same attribute and an unchanged attribute value doesn't restart the animation.
	//
	// The `cookieConsent*Close` events are dispatched in both cases, they are a public API for
	// external scripts (analytics, GTM).
	private closeCookieConsent(): void {
		if (!this.element && !modal.isOpen) {
			return
		}

		// Both the closing animation and the duration below are defined on the outermost element,
		// which is ours in the first case and the modal's one in the second.
		const modalElement = this.element || modal.element

		document.dispatchEvent(new CustomEvent('cookieConsentBeforeClose', { bubbles: true }))

		const closingDuration = parseInt(
			getComputedStyle(modalElement).getPropertyValue('--pd-modal-closing-duration') || '0'
		)

		if (this.element) {
			this.element.dataset.modalClosing = 'true'
		} else {
			modal.close()
		}

		setTimeout(() => {
			this.element?.remove()

			// Once the bar is gone, any further consent can only come from the modal.
			this.element = null

			document.dispatchEvent(new CustomEvent('cookieConsentAfterClose', { bubbles: true }))
		}, closingDuration)
	}
}
